import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import {
  RequestCreateCommentTemplate,
  RequestUpdateCommentTemplate,
} from '@/schema/commentTemplate'

import { DEFAULT_STALE_TIME_MS, TABLE_PAGE_SIZE } from '../../utils/constants'

import { commentTemplateApi } from './services'
import { CommentTemplateFilters } from './types'

const QUERY_KEY_COMMENT_TEMPLATE_LIST = 'comment-template-list'
const QUERY_KEY_COMMENT_TEMPLATE_DETAIL = 'comment-template-detail'

const COMMENT_TEMPLATE_LIST_QUERY_KEY = (params?: CommentTemplateFilters) => [
  QUERY_KEY_COMMENT_TEMPLATE_LIST,
  params,
]
const COMMENT_TEMPLATE_DETAIL_QUERY_KEY = (id: string) => [
  QUERY_KEY_COMMENT_TEMPLATE_DETAIL,
  id,
]

const DEFAULT_SORT_BY = '+name'

const invalidateCommentTemplateQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  templateId?: string
) => {
  queryClient.invalidateQueries({
    queryKey: [QUERY_KEY_COMMENT_TEMPLATE_LIST],
    refetchType: 'active',
  })

  if (templateId) {
    queryClient.invalidateQueries({
      queryKey: COMMENT_TEMPLATE_DETAIL_QUERY_KEY(templateId),
      refetchType: 'active',
    })
  }
}

export const useCommentTemplateList = (params?: CommentTemplateFilters) => {
  const limit = params?.limit || TABLE_PAGE_SIZE
  const sort_by = params?.sort_by || DEFAULT_SORT_BY
  const q = params?.q || undefined

  return useInfiniteQuery({
    queryKey: COMMENT_TEMPLATE_LIST_QUERY_KEY({ q, limit, sort_by }),
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const response = await commentTemplateApi.getCommentTemplates({
        q,
        limit,
        offset: pageParam,
        sort_by,
      })
      return response.data
    },
    getNextPageParam: (lastPage, allPages) => {
      const currentOffset = (allPages.length - 1) * limit
      const nextOffset = currentOffset + limit
      return lastPage.length === limit ? nextOffset : undefined
    },
    initialPageParam: 0,
    staleTime: DEFAULT_STALE_TIME_MS,
  })
}

export const useCommentTemplateById = (id: string | undefined) => {
  return useQuery({
    queryKey: COMMENT_TEMPLATE_DETAIL_QUERY_KEY(id || ''),
    queryFn: async () => {
      if (!id) {
        throw new Error('Comment template ID is required')
      }
      const response = await commentTemplateApi.getCommentTemplateById(id)
      return response.data?.[0]
    },
    enabled: !!id,
    staleTime: DEFAULT_STALE_TIME_MS,
  })
}

export const useCreateCommentTemplate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: RequestCreateCommentTemplate) =>
      commentTemplateApi.createCommentTemplate(data),
    onSuccess: () => {
      invalidateCommentTemplateQueries(queryClient)
    },
  })
}

export const useUpdateCommentTemplate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: RequestUpdateCommentTemplate
    }) => commentTemplateApi.updateCommentTemplate(id, data),
    onSuccess: (_, variables) => {
      invalidateCommentTemplateQueries(queryClient, variables.id)
    },
  })
}

export const useDeleteCommentTemplate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => commentTemplateApi.deleteCommentTemplate(id),
    onSuccess: () => {
      invalidateCommentTemplateQueries(queryClient)
    },
  })
}
